import { useState } from 'react';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';

export default function ExportButton({ 
  type = 'excel', // 'excel' or 'pdf'
  endpoint,
  filename,
  data,
  filters = {},
  className = '',
  children,
  size = 'sm',
  variant = 'primary'
}) {
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    if (isExporting) return;

    try {
      setIsExporting(true);

      let response;
      // Generar el XLSX/PDF puede superar el timeout default de 10s del cliente
      const config = {
        responseType: 'blob',
        params: filters,
        timeout: 60000
      };

      if (data && (endpoint.includes('custom-report') || endpoint.includes('POST'))) {
        response = await api.post(endpoint, data, {
          responseType: 'blob',
          timeout: 60000
        });
      } else {
        response = await api.get(endpoint, config);
      }

      // Create blob and download
      const blob = new Blob([response.data], { 
        type: type === 'excel' 
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/pdf' 
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or use provided filename
      const contentDisposition = response.headers['content-disposition'];
      let downloadFilename = filename;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          downloadFilename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      if (!downloadFilename) {
        const extension = type === 'excel' ? 'xlsx' : 'pdf';
        downloadFilename = `export_${Date.now()}.${extension}`;
      }

      link.download = downloadFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Show success message
      const recordsCount = response.headers['x-export-records'];
      if (recordsCount) {
        console.log(`✅ Exportados ${recordsCount} registros`);
      }

    } catch (error) {
      console.error('Erro na exportação:', error);
      alert('Erro ao exportar dados. Tente novamente.');
    } finally {
      setIsExporting(false);
    }
  };

  const sizeClasses = {
    xs: 'px-2.5 py-1.5 text-xs',
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-4 py-2 text-base',
    xl: 'px-6 py-3 text-base'
  };

  const variantClasses = {
    primary: 'bg-blue-600 hover:bg-blue-700 text-white border-transparent',
    secondary: 'bg-white hover:bg-gray-50 text-gray-700 border-gray-300',
    success: 'bg-green-600 hover:bg-green-700 text-white border-transparent',
    outline: 'bg-white hover:bg-gray-50 text-blue-600 border-blue-300'
  };

  return (
    <button
      onClick={handleExport}
      disabled={isExporting}
      className={`
        inline-flex items-center border font-medium rounded-md shadow-sm
        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
        disabled:opacity-50 disabled:cursor-not-allowed
        ${sizeClasses[size]}
        ${variantClasses[variant]}
        ${className}
      `}
    >
      {isExporting ? (
        <>
          <div className="animate-spin -ml-1 mr-2 h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
          Exportando...
        </>
      ) : (
        <>
          <ArrowDownTrayIcon className="-ml-1 mr-2 h-4 w-4" />
          {children || `Exportar ${type.toUpperCase()}`}
        </>
      )}
    </button>
  );
}

// Preset components for common exports
export function ExportContactsButton({ filters = {}, ...props }) {
  return (
    <ExportButton
      type="excel"
      endpoint="/exports/contacts/excel"
      filename="contatos.xlsx"
      filters={filters}
      {...props}
    >
      Exportar Contatos
    </ExportButton>
  );
}

export function ExportOpportunitiesButton({ filters = {}, ...props }) {
  return (
    <ExportButton
      type="excel"
      endpoint="/exports/opportunities/excel"
      filename="oportunidades.xlsx"
      filters={filters}
      {...props}
    >
      Exportar Oportunidades
    </ExportButton>
  );
}

export function ExportActivitiesButton({ filters = {}, ...props }) {
  return (
    <ExportButton
      type="excel"
      endpoint="/exports/activities/excel"
      filename="atividades.xlsx"
      filters={filters}
      {...props}
    >
      Exportar Atividades
    </ExportButton>
  );
}

export function ExportDashboardButton({ ...props }) {
  return (
    <ExportButton
      type="pdf"
      endpoint="/exports/dashboard/pdf"
      filename="dashboard.pdf"
      variant="outline"
      {...props}
    >
      Exportar Dashboard
    </ExportButton>
  );
}